import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, WifiOff, Share, X, MoreHorizontal, PlusSquare, Smartphone, Check } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;
    
    const isiPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent) || isiPadOS;

    setIsInstalled(isStandalone);
    setIsIOS(isIOSDevice);

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return false;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
      setDeferredPrompt(null);
      return true;
    }
    return false;
  };

  return {
    isInstallable: !!deferredPrompt,
    isInstalled,
    isIOS,
    install,
  };
}

export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}

export const PWAInstallCard: React.FC<{className?: string}> = ({ className }) => {
  const { isInstallable, isInstalled, isIOS, install } = usePWAInstall();
  const [showPopup, setShowPopup] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const dismissedAt = localStorage.getItem('pwa_dismissed_at');
    if (dismissedAt) {
      if (Date.now() - parseInt(dismissedAt, 10) < 7 * 24 * 60 * 60 * 1000) {
        setDismissed(true);
      } else {
        localStorage.removeItem('pwa_dismissed_at');
      }
    }
  }, []);

  if (isInstalled || dismissed) return null;

  return (
    <>
      <div className={`rounded-2xl bg-[#161618] border border-[#27272A] p-4 relative ${className}`}>
        <button 
          className="absolute top-2 right-2 p-1 text-[#737373] hover:text-white" 
          onClick={() => { setDismissed(true); localStorage.setItem('pwa_dismissed_at', Date.now().toString()); }}
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
        <h3 className="flex items-center gap-1.5 text-sm font-bold text-white mb-1">
          Install Quick!Bible
          <span className="text-[#F59E0B] text-[8px] font-extralight font-serif tracking-[0.2em] uppercase translate-y-[0.5px]">BETA</span>
        </h3>
        <p className="text-xs text-[#A3A3A3] mb-3">Add to your home screen for lightning-fast access & offline study.</p>
        <button
          className="w-full py-2 bg-[#F97316] hover:bg-[#FB923C] text-white text-xs font-bold rounded-lg transition-colors shadow-md"
          onClick={() => setShowPopup(true)}
        >
          Install App
        </button>
      </div>

      {showPopup && createPortal(
        <InstallPopup 
          onClose={() => setShowPopup(false)} 
          isInstallable={isInstallable} 
          onNativeInstall={install}
          isIOS={isIOS}
        />,
        document.body
      )}
    </>
  );
};

export const PWAInstallButton: React.FC = () => {
  const { isInstallable, isInstalled, install, isIOS } = usePWAInstall();
  const [showPopup, setShowPopup] = useState(false);

  if (isInstalled) return null;

  return (
    <div className="relative">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowPopup(true);
        }}
        className="w-9 h-9 flex items-center justify-center text-[#F97316] hover:bg-[#1C1C1C] rounded-full active:opacity-70 transition-colors shrink-0"
        aria-label="Install App"
        title="Install Quick!Bible BETA"
      >
        <Download className="w-5 h-5" strokeWidth={2.5} />
      </button>

      {showPopup && createPortal(
        <InstallPopup 
          onClose={() => setShowPopup(false)} 
          isInstallable={isInstallable} 
          onNativeInstall={install}
          isIOS={isIOS}
        />,
        document.body
      )}
    </div>
  );
};

interface InstallPopupProps {
  onClose: () => void;
  isInstallable: boolean;
  onNativeInstall: () => Promise<boolean>;
  isIOS: boolean;
}

const InstallPopup: React.FC<InstallPopupProps> = ({ onClose, isInstallable, onNativeInstall, isIOS }) => {
  const [tab, setTab] = useState<'android' | 'ios'>(isIOS ? 'ios' : 'android');

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pointer-events-none">
      <div 
        className="fixed inset-0 z-40 bg-black/50 backdrop-blur-xs pointer-events-auto transition-opacity" 
        onClick={onClose} 
      />
      <div className="relative z-50 pointer-events-auto mb-6 sm:mb-0 w-[92%] max-w-md bg-[#141414] border border-[#27272A] rounded-2xl p-4 sm:p-5 shadow-2xl space-y-3.5 animate-slideUp text-white" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between pr-6">
          <div>
            <h3 className="flex items-center gap-2 text-base font-bold text-white tracking-tight">
              Install Quick!Bible
              <span className="text-[#F59E0B] text-[8px] font-extralight font-serif tracking-[0.2em] uppercase translate-y-[0.5px]">BETA</span>
            </h3>
          </div>
          <button 
            type="button" 
            onClick={onClose} 
            className="absolute top-3.5 right-3.5 p-1.5 rounded-full bg-[#1C1C1C] text-[#A3A3A3] hover:text-white transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {isInstallable && (
          <div className="bg-[#1C1C1C] border border-[#333] rounded-xl p-3 flex items-center justify-between">
            <span className="text-xs font-bold text-orange-400">Instant Installation Available</span>
            <button
              onClick={async () => {
                const success = await onNativeInstall();
                if (success) onClose();
              }}
              className="px-3 py-1.5 bg-[#F97316] hover:bg-[#FB923C] text-white text-xs font-bold rounded-lg shadow transition-colors shrink-0"
            >
              Install Now
            </button>
          </div>
        )}

        <div className="flex bg-[#1C1C1C] p-1 rounded-xl gap-1 border border-[#27272A]">
          <button 
            className={`flex-1 py-1.5 text-[11px] font-bold rounded-lg transition-all flex items-center justify-center gap-1 ${tab === 'android' ? 'bg-[#F97316] text-white shadow-sm' : 'text-[#A3A3A3] hover:text-white'}`}
            onClick={() => setTab('android')}
          >
            <Smartphone className="w-3.5 h-3.5" /> Android / Chrome
          </button>
          <button 
            className={`flex-1 py-1.5 text-[11px] font-bold rounded-lg transition-all flex items-center justify-center gap-1 ${tab === 'ios' ? 'bg-[#F97316] text-white shadow-sm' : 'text-[#A3A3A3] hover:text-white'}`}
            onClick={() => setTab('ios')}
          >
            <Share className="w-3.5 h-3.5" /> iPhone / iPad
          </button>
        </div>

        <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
          {tab === 'ios' ? (
            <>
              <StepRow number={1} icon={<MoreHorizontal className="w-4 h-4 text-white" />} text="Open the browser menu." />
              <StepRow number={2} icon={<Share className="w-4 h-4 text-white" />} text="Tap the <strong class='text-white font-bold'>Share</strong> button." />
              <StepRow number={3} icon={<PlusSquare className="w-4 h-4 text-white" />} text="Select <strong class='text-white font-bold'>Add to Home Screen</strong>." />
              <StepRow number={4} icon={<Check className="w-4 h-4 text-orange-400" />} text="Tap <strong class='text-white font-bold'>Add</strong> to complete the installation." />
            </>
          ) : (
            <>
              <StepRow number={1} icon={<MoreHorizontal className="w-4 h-4 text-white" />} text="Open the browser menu if necessary." />
              <StepRow number={2} icon={<Download className="w-4 h-4 text-white" />} text="Select <strong class='text-white font-bold'>Install App</strong> or <strong class='text-white font-bold'>Add to Home Screen</strong>." />
              <StepRow number={3} icon={<Check className="w-4 h-4 text-orange-400" />} text="Tap <strong class='text-white font-bold'>Install</strong> or <strong class='text-white font-bold'>Add</strong>." />
              <StepRow number={4} icon={<Smartphone className="w-4 h-4 text-white" />} text="Open <strong class='text-white font-bold'>Quick!Bible</strong> from your home screen." />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const StepRow: React.FC<{ number: number; icon: React.ReactNode; text: string }> = ({ number, icon, text }) => (
  <div className="flex items-center gap-3 bg-[#1C1C1C] border border-[#27272A] p-2.5 rounded-xl">
    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#262626] border border-[#333] shrink-0">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] font-bold text-orange-400 bg-orange-500/20 px-1 py-0.5 rounded">Step {number}</span>
      </div>
      <p className="text-xs font-medium text-white mt-0.5 leading-snug" dangerouslySetInnerHTML={{ __html: text }} />
    </div>
  </div>
);

export const OfflineIndicator: React.FC = () => {
  const isOnline = useOnlineStatus();
  if (isOnline) return null;

  return (
    <div
      role="status"
      className="fixed bottom-16 md:bottom-4 left-4 z-50 flex items-center gap-2 rounded-lg bg-amber-600 px-3.5 py-2 text-xs font-medium text-white shadow-lg"
    >
      <WifiOff className="w-4 h-4 shrink-0" />
      <span>Offline Mode — Using cached Scripture & PWA assets</span>
    </div>
  );
};
